import { useState } from "react";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import {
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
} from "@/lib/mutations";
import { useCategoriesQuery } from "@/lib/queries";

type Category = { id: number; name: string; sort_order: number };

export function Categories() {
  const { data, isPending, isError, error, refetch } = useCategoriesQuery();
  const categories = (data ?? []) as Category[];

  const createCategoryMutation = useCreateCategoryMutation();
  const updateCategoryMutation = useUpdateCategoryMutation();
  const deleteCategoryMutation = useDeleteCategoryMutation();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editSort, setEditSort] = useState(0);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSort, setNewSort] = useState(0);

  const isMutating =
    createCategoryMutation.isPending ||
    updateCategoryMutation.isPending ||
    deleteCategoryMutation.isPending;

  const startEdit = (cat: Category) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditSort(cat.sort_order);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;

    try {
      await updateCategoryMutation.mutateAsync({
        id: editingId,
        data: { name: editName.trim(), sort_order: editSort },
      });
      toast.success("Category updated");
      setEditingId(null);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"? Items in this category will also be deleted.`)) return;

    try {
      await deleteCategoryMutation.mutateAsync(id);
      toast.success(`Deleted "${name}"`);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;

    try {
      await createCategoryMutation.mutateAsync({ name: newName.trim(), sort_order: newSort });
      toast.success("Category created");
      setAdding(false);
      setNewName("");
      setNewSort(0);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  if (isPending) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border">
        <p className="text-[13px] text-muted-foreground">{getErrorMessage(error)}</p>
        <button
          onClick={() => refetch()}
          className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
        >
          Retry
        </button>
      </div>
    );
  }

  const inputClass =
    "flex h-8 w-full rounded-md border bg-background px-2.5 text-[13px] transition-all focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground";

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight">Categories</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Organize your menu items.</p>
        </div>
        <button
          onClick={() => {
            setAdding(true);
            setNewName("");
            setNewSort(categories.length);
          }}
          disabled={isMutating}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-[12px] font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Plus className="size-3.5" />
          Add category
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Name
              </th>
              <th className="sm:w-28 px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Order
              </th>
              <th className="sm:w-24 px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) =>
              editingId === cat.id ? (
                <tr key={cat.id} className="border-b last:border-0 bg-muted/10">
                  <td className="px-4 py-2.5">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className={inputClass}
                      autoFocus
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <input
                      type="number"
                      value={editSort}
                      onChange={(e) => setEditSort(Number(e.target.value))}
                      className={`${inputClass} w-16`}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={saveEdit}
                        disabled={isMutating}
                        className="rounded-md p-1.5 text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Check className="size-3.5" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={isMutating}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={cat.id} className="border-b last:border-0 transition-colors hover:bg-muted/20">
                  <td className="px-4 py-3 text-[13px] font-medium">{cat.name}</td>
                  <td className="px-4 py-3 text-[13px] font-mono text-muted-foreground">{cat.sort_order}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => startEdit(cat)}
                        disabled={isMutating}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Pencil className="size-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id, cat.name)}
                        disabled={isMutating}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
            {adding && (
              <tr className="border-b last:border-0 bg-muted/10">
                <td className="px-4 py-2.5">
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Category name"
                    className={inputClass}
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  />
                </td>
                <td className="px-4 py-2.5">
                  <input
                    type="number"
                    value={newSort}
                    onChange={(e) => setNewSort(Number(e.target.value))}
                    className={`${inputClass} w-16`}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={handleAdd}
                      disabled={isMutating}
                      className="rounded-md p-1.5 text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Check className="size-3.5" />
                    </button>
                    <button
                      onClick={() => setAdding(false)}
                      disabled={isMutating}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {categories.length === 0 && !adding && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-[13px] text-muted-foreground">
                  No categories yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
